import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { CalendarClock, FilePlus, MessageCircle, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { httpClient } from '../../common/httpclient';
import { messageFromApiErrorBody } from '../../common/apiErrorMessage';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import StatusChip from '../../component/common/StatusChip';
import { useEnterpriseConfigContext } from '../../component/context/EnterpriseConfigContext';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import MobileUpcoming from '../../component/serviceorder/MobileUpcoming';

// Filtro de status: "default" = vencido + a vencer (padrão da API).
const STATUS_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'default', label: 'Vencidos e a vencer' },
    { value: 'vencido', label: 'Vencidos' },
    { value: 'a_vencer', label: 'A vencer' },
    { value: 'em_dia', label: 'Em dia' },
    
];

const TB_FIELD_RADIUS = '9px';

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');
const formatKm = (value) => (value === null || value === undefined ? '-' : `${Number(value).toLocaleString('pt-BR')} km`);
const plural = (n, singular, pluralText) => (Math.abs(n) === 1 ? singular : pluralText);

const describeDays = (days) => {
    if (days === null || days === undefined) return '';
    if (days < 0) return `${Math.abs(days)} ${plural(days, 'dia', 'dias')} de atraso`;
    if (days === 0) return 'vence hoje';
    return `em ${days} ${plural(days, 'dia', 'dias')}`;
};

const describeKmRemaining = (remaining) => {
    if (remaining === null || remaining === undefined) return '';
    if (remaining < 0) return `${formatKm(Math.abs(remaining))} acima`;
    if (remaining === 0) return 'atingido';
    return `faltam ${formatKm(remaining)}`;
};

const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

const emptyFilters = {
    status: 'all',
    customerId: '',
    vehicle: '',
    serviceId: '',
    nextFrom: null,
    nextTo: null,
};

export default function UpcomingServices() {
    const navigate = useNavigate();
    const { settings, isLoading: loadingSettings } = useEnterpriseConfigContext();
    const usesVehicle = Boolean(settings?.os_use_vehicle);
    const isMobileShell = useIsMobileShell();
    const { hasPermission } = usePermissions();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação.
    const MOBILE_PAGE_SIZE = 20;
    const [mobilePage, setMobilePage] = useState(0);

    const [filters, setFilters] = useState(emptyFilters);
    // Filtros efetivamente aplicados na busca (só mudam em "Buscar"/"Limpar").
    const [appliedFilters, setAppliedFilters] = useState(emptyFilters);
    // Paginação server-side (page é 0-based na tela; a API usa 1-based).
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(8);
    const [totalCount, setTotalCount] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [customers, setCustomers] = useState([]);
    const [services, setServices] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');
    const [sendingKey, setSendingKey] = useState('');
    const [confirmRow, setConfirmRow] = useState(null);
    // Mantém os dados da linha durante a animação de fechamento do modal.
    const lastConfirmRow = useRef(null);
    if (confirmRow) lastConfirmRow.current = confirmRow;
    const dialogRow = confirmRow || lastConfirmRow.current;
    const dialogSending = Boolean(dialogRow) && sendingKey === `${dialogRow.vehicle_id}:${dialogRow.service_id}`;
    // Geração de OS: linha em confirmação e flag de requisição (evita clique duplo / OS duplicada).
    const [generateRow, setGenerateRow] = useState(null);
    const [generating, setGenerating] = useState(false);
    const lastGenerateRow = useRef(null);
    if (generateRow) lastGenerateRow.current = generateRow;
    const generateDialogRow = generateRow || lastGenerateRow.current;

    const reminderKey = (row) => `${row.vehicle_id}:${row.service_id}`;

    // Envio manual do aviso por WhatsApp (mesma mensagem do lembrete automático).
    const handleSendReminder = async (row) => {
        const key = reminderKey(row);
        setSendingKey(key);
        try {
            await httpClient.post('/vehicles/recurring-services/reminder', {
                vehicle_id: row.vehicle_id,
                service_id: row.service_id,
            });
            setMessage(`Aviso enviado por WhatsApp para ${row.customer_name || 'o cliente'}`);
            setSeverity('success');
        } catch (error) {
            console.error('Erro ao enviar aviso por WhatsApp:', error);
            const data = error.response?.data;
            setMessage((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao enviar o aviso por WhatsApp');
            setSeverity('error');
        } finally {
            setSendingKey('');
            setConfirmRow(null);
            setOpenSnackbar(true);
        }
    };

    // "Gerar OS": pede confirmação e cria a OS de fato no backend (cliente + veículo + serviço).
    const handleGenerateOrder = (row) => setGenerateRow(row);

    const handleConfirmGenerate = async () => {
        if (!generateRow || generating) return;
        setGenerating(true);
        try {
            const response = await httpClient.post('/orderservices/from-service', {
                customer_id: generateRow.customer_id,
                vehicle_id: generateRow.vehicle_id,
                service_id: generateRow.service_id,
            });
            const id = response.data?.id;
            setGenerateRow(null);
            if (id) {
                navigate(`/serviceorder/${id}/operator`, { state: { id, fromFlow: true }, replace: true });
                return;
            }
            // Sem ID na resposta: permanece na lista, que passa a refletir a nova OS.
            setMessage('Ordem de serviço criada com sucesso');
            setSeverity('success');
            setOpenSnackbar(true);
            setReloadKey((k) => k + 1);
        } catch (error) {
            console.error('Erro ao gerar ordem de serviço:', error);
            const data = error.response?.data;
            setMessage((typeof data === 'string' && data.trim()) || messageFromApiErrorBody(data) || 'Erro ao gerar a ordem de serviço');
            setSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setGenerating(false);
        }
    };

    const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

    // Busca a página atual no servidor sempre que filtros aplicados/página/tamanho mudam.
    useEffect(() => {
        if (loadingSettings || !usesVehicle) return undefined;
        let cancelled = false;

        const requestPage = isMobileShell ? mobilePage : page;
        const params = { status: appliedFilters.status, page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedFilters.customerId) params.customer_id = appliedFilters.customerId;
        if (appliedFilters.vehicle.trim()) params.vehicle = appliedFilters.vehicle.trim();
        if (appliedFilters.serviceId) params.service_id = appliedFilters.serviceId;
        if (appliedFilters.nextFrom?.isValid?.()) params.next_from = appliedFilters.nextFrom.format('YYYY-MM-DD');
        if (appliedFilters.nextTo?.isValid?.()) params.next_to = appliedFilters.nextTo.format('YYYY-MM-DD');

        setLoading(true);
        setErrorMessage('');
        httpClient.get('/vehicles/recurring-services/upcoming', { params })
            .then((response) => {
                if (cancelled) return;
                const received = Array.isArray(response.data?.items) ? response.data.items : [];
                if (isMobileShell && requestPage > 0) {
                    setItems((prev) => [...prev, ...received.filter((row) => !prev.some((p) => reminderKey(p) === reminderKey(row)))]);
                } else {
                    setItems(received);
                }
                setTotalCount(response.data?.total || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Erro ao buscar próximos serviços:', error);
                setItems([]);
                setTotalCount(0);
                setErrorMessage(messageFromApiErrorBody(error?.response?.data) || 'Não foi possível carregar os próximos serviços.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingSettings, usesVehicle, appliedFilters, page, pageSize, reloadKey, isMobileShell, mobilePage]);

    useEffect(() => {
        if (loadingSettings || !usesVehicle) return;
        httpClient.get('/customers')
            .then((response) => setCustomers(Array.isArray(response.data) ? response.data : []))
            .catch((error) => console.error('Erro ao buscar clientes:', error));
        httpClient.get('/services/all')
            .then((response) => setServices(
                Array.isArray(response.data) ? response.data.filter((service) => service.is_recurring) : []
            ))
            .catch((error) => console.error('Erro ao buscar serviços:', error));
    }, [loadingSettings, usesVehicle]);

    // Aplicar filtros volta para a primeira página; reloadKey força nova busca
    // mesmo quando os filtros não mudaram (botão "Buscar" / "Tentar novamente").
    const handleSearch = () => {
        setAppliedFilters({ ...filters });
        setPage(0);
        setMobilePage(0);
        setReloadKey((k) => k + 1);
    };

    const handleClear = () => {
        setFilters(emptyFilters);
        setAppliedFilters(emptyFilters);
        setPage(0);
        setMobilePage(0);
    };

    // Mobile: chips Todos / Atrasados / 7 dias / 30 dias -> status + janela de datas da API
    // (`next_to`); a folha "Filtrar" aplica veículo, cliente e serviço.
    const mobileChip = (() => {
        if (appliedFilters.status === 'vencido') return 'late';
        if (appliedFilters.status === 'a_vencer' && appliedFilters.nextTo?.isValid?.()) {
            const days = appliedFilters.nextTo.startOf('day').diff(dayjs().startOf('day'), 'day');
            if (days === 7) return '7';
            if (days === 30) return '30';
        }
        return 'all';
    })();

    const applyMobile = (next) => {
        setFilters(next);
        setAppliedFilters(next);
        setPage(0);
        setMobilePage(0);
    };

    const handleMobileChip = (key) => {
        const base = { ...appliedFilters, nextFrom: null, nextTo: null };
        if (key === 'late') applyMobile({ ...base, status: 'vencido' });
        else if (key === '7') applyMobile({ ...base, status: 'a_vencer', nextTo: dayjs().add(7, 'day') });
        else if (key === '30') applyMobile({ ...base, status: 'a_vencer', nextTo: dayjs().add(30, 'day') });
        else applyMobile({ ...base, status: 'all' });
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    };

    const hasActiveFilters = Boolean(
        filters.status !== 'default' || filters.customerId || filters.vehicle || filters.serviceId || filters.nextFrom || filters.nextTo
    );

    const selectedCustomerName = customers.find((c) => String(c.ID) === String(filters.customerId))?.Name;
    const selectedServiceName = services.find((s) => String(s.id) === String(filters.serviceId))?.name;

    const columns = [
        {
            path: 'customer_name',
            name: 'Cliente',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography component="span" sx={{ display: 'block', fontSize: '13px', fontWeight: 700 }}>
                        {row.customer_name || '-'}
                    </Typography>
                    {row.customer_phone && (
                        <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
                            {row.customer_phone}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            path: 'license_plate',
            name: 'Veículo',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography component="span" sx={{ display: 'block', fontSize: '13px', fontWeight: 700 }}>
                        {row.license_plate || '-'}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
                        {row.vehicle_model || ''}
                    </Typography>
                </Box>
            ),
        },
        {
            path: 'service_name',
            name: 'Serviço',
            invisible: false,
            render: (row) => <Typography component="span" sx={{ fontSize: '13px' }}>{row.service_name}</Typography>,
        },
        {
            path: 'last_date',
            name: 'Última vez',
            invisible: false,
            render: (row) => (
                <Box>
                    <Typography component="span" sx={{ display: 'block', fontSize: '13px' }}>{formatDate(row.last_date)}</Typography>
                    <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
                        {row.last_order_number ? `OS ${row.last_order_number}` : ''}
                        {row.last_kilometer !== null && row.last_kilometer !== undefined ? ` · ${formatKm(row.last_kilometer)}` : ''}
                    </Typography>
                </Box>
            ),
        },
        {
            path: 'next_date',
            name: 'Próxima data',
            invisible: false,
            render: (row) => (
                row.next_date ? (
                    <Box>
                        <Typography component="span" sx={{ display: 'block', fontSize: '13px' }}>{formatDate(row.next_date)}</Typography>
                        <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
                            {describeDays(row.days_remaining)}
                        </Typography>
                    </Box>
                ) : <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>-</Typography>
            ),
        },
        {
            path: 'target_kilometer',
            name: 'KM alvo / atual',
            invisible: false,
            render: (row) => (
                row.target_kilometer !== null && row.target_kilometer !== undefined ? (
                    <Box>
                        <Typography component="span" sx={{ display: 'block', fontSize: '13px' }}>
                            {formatKm(row.target_kilometer)} / {formatKm(row.current_kilometer)}
                        </Typography>
                        <Typography component="span" sx={{ display: 'block', fontSize: '12px', color: 'text.secondary' }}>
                            {describeKmRemaining(row.km_remaining)}
                        </Typography>
                    </Box>
                ) : <Typography component="span" sx={{ fontSize: '13px', color: 'text.secondary' }}>-</Typography>
            ),
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="recurringService" value={row.status} />,
        },
        {
            path: 'actions',
            name: 'Ações',
            invisible: false,
            render: (row) => {
                const canGenerate = hasPermission('serviceorder.create');
                const sending = sendingKey === reminderKey(row);
                const noPhone = !String(row.customer_phone || '').trim();
                const noDate = !row.next_date;
                const tooltip = noPhone
                    ? 'Cliente sem telefone cadastrado'
                    : noDate
                        ? 'Serviço sem intervalo de tempo: aviso indisponível'
                        : 'Enviar aviso por WhatsApp';
                return (
                    <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
                        {canGenerate && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<FilePlus size={14} strokeWidth={2} />}
                                onClick={() => handleGenerateOrder(row)}
                                sx={{ height: '30px', whiteSpace: 'nowrap' }}
                            >
                                Gerar OS
                            </Button>
                        )}
                        <Tooltip title={tooltip}>
                            <span>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={noPhone || noDate || Boolean(sendingKey)}
                                    startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <MessageCircle size={14} strokeWidth={2} />}
                                    onClick={() => setConfirmRow(row)}
                                    sx={{ height: '30px', whiteSpace: 'nowrap' }}
                                >
                                    {sending ? 'Enviando…' : 'Avisar'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ];

    // Confirmação do aviso por WhatsApp (compartilhada entre desktop e mobile).
    const renderReminderDialog = () => (
        <Dialog
            open={Boolean(confirmRow)}
            onClose={() => { if (!dialogSending) setConfirmRow(null); }}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle sx={{ fontSize: '17px', fontWeight: 800 }}>Enviar aviso por WhatsApp</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mb: 2 }}>
                    Confirma o envio do aviso de serviço recorrente para o cliente abaixo?
                </Typography>
                {dialogRow && (
                    <Stack spacing={1.25}>
                        {[
                            ['Cliente', dialogRow.customer_name || '-'],
                            ['Telefone', dialogRow.customer_phone || '-'],
                            ['Serviço', dialogRow.service_name || '-'],
                            ['Veículo', [dialogRow.vehicle_model, dialogRow.license_plate].filter(Boolean).join(' - ') || '-'],
                            ['Data prevista', formatDate(dialogRow.next_date)],
                        ].map(([label, value]) => (
                            <Box key={label}>
                                <TbLabel>{label}</TbLabel>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>{value}</Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setConfirmRow(null)} disabled={dialogSending}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    disabled={dialogSending}
                    startIcon={dialogSending ? <CircularProgress size={14} color="inherit" /> : <MessageCircle size={14} strokeWidth={2} />}
                    onClick={() => handleSendReminder(confirmRow)}
                >
                    {dialogSending ? 'Enviando…' : 'Enviar'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Confirmação da geração de OS (compartilhada entre desktop e mobile).
    const renderGenerateDialog = () => (
        <Dialog
            open={Boolean(generateRow)}
            onClose={() => { if (!generating) setGenerateRow(null); }}
            maxWidth="xs"
            fullWidth
        >
            <DialogTitle sx={{ fontSize: '17px', fontWeight: 800 }}>Gerar ordem de serviço</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mb: 2 }}>
                    Será criada uma ordem de serviço com o serviço abaixo. Confirma?
                </Typography>
                {generateDialogRow && (
                    <Stack spacing={1.25}>
                        {[
                            ['Cliente', generateDialogRow.customer_name || '-'],
                            ['Veículo', [generateDialogRow.vehicle_model, generateDialogRow.license_plate].filter(Boolean).join(' - ') || '-'],
                            ['Serviço', generateDialogRow.service_name || '-'],
                        ].map(([label, value]) => (
                            <Box key={label}>
                                <TbLabel>{label}</TbLabel>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 600 }}>{value}</Typography>
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setGenerateRow(null)} disabled={generating}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    disabled={generating}
                    startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <FilePlus size={14} strokeWidth={2} />}
                    onClick={handleConfirmGenerate}
                >
                    {generating ? 'Gerando…' : 'Gerar OS'}
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Shell mobile: lista em cartões do protótipo `ordens/proximos.html` (mesmos dados, filtros e aviso por WhatsApp).
    if (isMobileShell) {
        return (
            <PageScaffold links={[]} allowBleed titlePageTarget={'Próximos Serviços'} setOpenSnackbar={setOpenSnackbar} setMessage={setMessage} openSnackbar={openSnackbar} severity={severity} message={message}>
                <MobileUpcoming
                    usesVehicle={usesVehicle}
                    loadingSettings={loadingSettings}
                    items={items}
                    loading={loading}
                    errorMessage={errorMessage}
                    onRetry={handleSearch}
                    totalCount={totalCount}
                    hasMore={items.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    chip={mobileChip}
                    onChipChange={handleMobileChip}
                    filters={{ vehicle: appliedFilters.vehicle, customerId: appliedFilters.customerId, serviceId: appliedFilters.serviceId }}
                    onApplyFilters={(draft) => applyMobile({ ...appliedFilters, ...draft })}
                    onClearFilters={() => applyMobile({ ...appliedFilters, vehicle: '', customerId: '', serviceId: '' })}
                    customers={customers}
                    services={services}
                    canGenerate={hasPermission('serviceorder.create')}
                    onGenerate={handleGenerateOrder}
                    onReminder={setConfirmRow}
                    sendingKey={sendingKey}
                    reminderKey={reminderKey}
                />
                {renderReminderDialog()}
                {renderGenerateDialog()}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold links={[]} titlePageTarget={'Próximos Serviços'} setOpenSnackbar={setOpenSnackbar} setMessage={setMessage} openSnackbar={openSnackbar} severity={severity} message={message}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                    }}>
                        <CalendarClock size={18} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Próximos Serviços
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Serviços e revisões recorrentes a refazer, com base no histórico de ordens de serviço.
                        </Typography>
                    </Box>
                </Box>

                {!loadingSettings && !usesVehicle ? (
                    <Alert severity="info">
                        A recorrência de serviços depende do veículo do cliente. Ative a opção "Usa veículo" em Configurações para usar esta tela.
                    </Alert>
                ) : (
                    <>
                        <FilterToolbar
                            plain
                            primaryIndex={3}
                            activeCount={
                                (filters.status !== 'default' ? 1 : 0) + (filters.customerId ? 1 : 0) + (filters.serviceId ? 1 : 0) +
                                (filters.vehicle ? 1 : 0) + (filters.nextFrom || filters.nextTo ? 1 : 0)
                            }
                            onClear={handleClear}
                            onApply={handleSearch}
                            primary={(
                                <Box sx={{ flex: '0 0 200px' }}>
                                    <TbLabel>Veículo</TbLabel>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="Placa ou modelo…"
                                        value={filters.vehicle}
                                        onChange={(e) => setFilter('vehicle', e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search size={15} strokeWidth={2} />
                                                </InputAdornment>
                                            ),
                                            sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                        }}
                                    />
                                </Box>
                            )}
                            actions={(
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    {hasActiveFilters && (
                                        <Button
                                            variant="text"
                                            onClick={handleClear}
                                            sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                                        >
                                            Limpar
                                        </Button>
                                    )}
                                    <Button
                                        variant="contained"
                                        startIcon={<Search size={15} strokeWidth={2} />}
                                        onClick={handleSearch}
                                        sx={{ height: 40, px: 2.25 }}
                                    >
                                        Buscar
                                    </Button>
                                </Stack>
                            )}
                        >
                                <Box sx={{ minWidth: 190 }}>
                                    <TbLabel>Status</TbLabel>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={filters.status}
                                            onChange={(e) => setFilter('status', e.target.value)}
                                            sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                        >
                                            {STATUS_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ minWidth: 190 }} data-wide>
                                    <TbLabel>Cliente</TbLabel>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={filters.customerId}
                                            displayEmpty
                                            onChange={(e) => setFilter('customerId', e.target.value)}
                                            renderValue={() => selectedCustomerName || 'Todos'}
                                            sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            {customers.map((customer) => (
                                                <MenuItem key={customer.ID} value={customer.ID}>{customer.Name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ minWidth: 190 }} data-wide>
                                    <TbLabel>Serviço</TbLabel>
                                    <FormControl size="small" fullWidth>
                                        <Select
                                            value={filters.serviceId}
                                            displayEmpty
                                            onChange={(e) => setFilter('serviceId', e.target.value)}
                                            renderValue={() => selectedServiceName || 'Todos'}
                                            sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                        >
                                            <MenuItem value="">Todos</MenuItem>
                                            {services.map((service) => (
                                                <MenuItem key={service.id} value={service.id}>{service.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box>
                                    <TbLabel>Próxima data</TbLabel>
                                    <DesktopPeriodSelector
                                        start={filters.nextFrom}
                                        end={filters.nextTo}
                                        onChange={(start, end) => setFilters((prev) => ({ ...prev, nextFrom: start, nextTo: end }))}
                                        showRefresh={false}
                                        showShortcuts={false}
                                        dualMonth
                                        align="start"
                                    />
                                </Box>

                        </FilterToolbar>

                        <FilterChips>
                            {filters.customerId && (
                                <Chip
                                    label={`Cliente: ${selectedCustomerName || 'N/A'}`}
                                    size="small"
                                    onDelete={() => setFilter('customerId', '')}
                                    deleteIcon={<X size={12} strokeWidth={2.5} />}
                                    sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                                />
                            )}
                            {filters.serviceId && (
                                <Chip
                                    label={`Serviço: ${selectedServiceName || 'N/A'}`}
                                    size="small"
                                    onDelete={() => setFilter('serviceId', '')}
                                    deleteIcon={<X size={12} strokeWidth={2.5} />}
                                    sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                                />
                            )}
                            {filters.nextFrom?.isValid?.() && filters.nextTo?.isValid?.() && (
                                <Chip
                                    label={`Próxima data: ${filters.nextFrom.format('DD/MM/YYYY')} – ${filters.nextTo.format('DD/MM/YYYY')}`}
                                    size="small"
                                    onDelete={() => setFilters((prev) => ({ ...prev, nextFrom: null, nextTo: null }))}
                                    deleteIcon={<X size={12} strokeWidth={2.5} />}
                                    sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                                />
                            )}
                            <Chip
                                label={`${totalCount} serviço${totalCount === 1 ? '' : 's'} encontrado${totalCount === 1 ? '' : 's'}`}
                                size="small"
                                sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                            />
                        </FilterChips>
                        {/* Os chips acima refletem os filtros em edição; a busca só é feita ao clicar em "Buscar". */}

                        {errorMessage ? (
                            <Alert severity="error" action={<Button color="inherit" size="small" onClick={handleSearch}>Tentar novamente</Button>}>
                                {errorMessage}
                            </Alert>
                        ) : !loading && items.length === 0 ? (
                            <Card>
                                <CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 0.5 }}>
                                        Nenhum serviço encontrado
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                                        Não há serviços recorrentes para os filtros selecionados. Serviços só aparecem aqui depois de executados em uma ordem de serviço com veículo.
                                    </Typography>
                                </CardContent>
                            </Card>
                        ) : (
                            <DefaultTable
                                colunms={columns}
                                data={items}
                                loading={loading}
                                onRowClick={(row) => row.last_order_id && navigate(`/serviceorders/${row.last_order_id}/view`)}
                                rowPerPage={pageSize}
                                rowsPerPageOptions={[5, 8, 15]}
                                itemLabel="serviços"
                                serverPagination={{
                                    page,
                                    rowsPerPage: pageSize,
                                    total: totalCount,
                                    onPageChange: setPage,
                                    onRowsPerPageChange: handlePageSizeChange,
                                }}
                            />
                        )}
                    </>
                )}
            </Box>

            {renderReminderDialog()}
            {renderGenerateDialog()}
        </PageScaffold>
    );
}
